import React, { useEffect, useState } from 'react'
import { Alert, Button, Collapse, Input, InputNumber, Tag, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import fetchAPI from '../../../lib/utils/fetch-api'
import { AdditionalField, ReviewValue, moneyValue } from './review/result-model'

export type InvoiceField = {
  code: string
  label: string
  scope: 'document' | 'line'
  value_type: 'text' | 'integer' | 'decimal' | 'money'
  source_field?: string
  source_labels: string[]
  editable: boolean
}
export function useSupplyUiConfig() {
  const userId = useSelector((state: any) => state.session?.sessionData?.user?.id)
  const [config, setConfig] = useState<{
    userId?: number
    ready: boolean
    can_open_chat: boolean
    accounting?: { warehouse_name?: string; warehouse_id?: string; company_id?: string }
    invoice_fields: InvoiceField[]
    error?: string
  }>({ ready: false, can_open_chat: false, invoice_fields: [] })
  useEffect(() => {
    let active = true
    fetchAPI('/api/supply/ui-config')
      .then(result => {
        if (!result.data || !Array.isArray(result.data.invoice_fields))
          throw new Error(result.error || 'Не удалось загрузить настройки накладных')
        if (active) setConfig({ ...result.data, ready: true, userId })
      })
      .catch(cause => {
        if (active)
          setConfig({
            userId,
            ready: true,
            can_open_chat: false,
            invoice_fields: [],
            error: cause.message,
          })
      })
    return () => {
      active = false
    }
  }, [userId])
  return config.userId === userId
    ? config
    : { ready: false, can_open_chat: false, accounting: undefined, invoice_fields: [] as InvoiceField[] }
}
const own = (value: any, key: string) => Object.prototype.hasOwnProperty.call(value || {}, key)
export const invoiceFieldCode = (field: AdditionalField, definitions: InvoiceField[]) =>
  field.code
    ? definitions.find(item => item.code === field.code)?.code
    : definitions.find(item => item.source_labels.includes(field.label))?.code
export function invoiceValue(
  field: InvoiceField,
  source: any,
  draft: any = {},
): ReviewValue | ReviewValue[] {
  if (own(draft.field_values, field.code)) return draft.field_values[field.code]
  if (field.source_field) {
    const values = field.scope === 'line' ? draft.values : draft.header
    if (own(values, field.source_field)) return values[field.source_field]
    if (source[field.source_field] != null && source[field.source_field] !== '')
      return source[field.source_field]
  }
  const values = (source.additional_fields || []).flatMap((item: AdditionalField, index: number) =>
    invoiceFieldCode(item, [field]) === field.code
      ? [own(draft.additional_values, String(index)) ? draft.additional_values[index] : item.value]
      : [],
  )
  return values.length ? values : null
}
export const invoiceDisplay = (
  value: ReviewValue | ReviewValue[] | undefined,
  type = 'text',
): string => {
  if (Array.isArray(value)) return value.map(item => invoiceDisplay(item, type)).join('; ')
  if (value == null || value === '') return 'Не указано'
  if (type === 'money') {
    const amount = moneyValue(value)
    return amount === '—' ? String(value) : amount
  }
  return String(value)
}
export const invoiceHasValue = (value: ReviewValue | ReviewValue[] | undefined): boolean =>
  Array.isArray(value) ? value.some(invoiceHasValue) : value != null && String(value).trim() !== ''

export function InvoiceFieldsEditor({
  definitions,
  source,
  draft,
  onChange,
  disabled,
  title,
  focus,
  presentation = 'collapse',
  hideEmpty = false,
}: {
  definitions: InvoiceField[]
  source: any
  draft: any
  onChange: (values: Record<string, ReviewValue>) => void
  disabled?: boolean
  title: string
  focus?: { field: string; request: number }
  presentation?: 'collapse' | 'plain'
  hideEmpty?: boolean
}) {
  const [activeKey, setActiveKey] = useState<string[]>([])
  const [showAll, setShowAll] = useState(false)
  useEffect(() => {
    if (!focus) return
    setActiveKey(['upd'])
    setShowAll(true)
    const timer = setTimeout(() => {
      const input = document.getElementById(`supply-upd-${focus.field}`)
      input?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      input?.focus()
    }, 100)
    return () => clearTimeout(timer)
  }, [focus])
  if (!definitions.length) return null
  const uncertain = (source.additional_fields || []).filter(
    (field: AdditionalField) =>
      field.certainty === 'uncertain' && invoiceFieldCode(field, definitions),
  )
  const visible = definitions.filter(
    field =>
      !hideEmpty ||
      showAll ||
      invoiceHasValue(invoiceValue(field, source, draft)) ||
      invoiceHasValue(invoiceValue(field, source)) ||
      uncertain.some((item: AdditionalField) => invoiceFieldCode(item, definitions) === field.code),
  )
  const multiline = (code: string) =>
    code.endsWith('_address') ||
    ['consignor', 'consignee', 'supply_basis', 'contract', 'shipment_document'].includes(code)
  const content = (
    <>
      {hideEmpty && (
        <div className="supply-check__optional-fields">
          <span>{showAll ? 'Все поля УПД' : 'Заполненные поля УПД'}</span>
          <Button type="link" size="small" onClick={() => setShowAll(value => !value)}>
            {showAll ? 'Скрыть пустые' : 'Показать все поля'}
          </Button>
        </div>
      )}
      {!visible.length && (
        <p className="supply-check__empty-fields">Дополнительные поля УПД не заполнены.</p>
      )}
      {!!uncertain.length && (
        <Alert
          type="warning"
          showIcon
          message="Есть неуверенно распознанные реквизиты — сверьте их с оригиналом"
        />
      )}
      <div className="supply-check__additional-grid">
        {visible.map(field => {
          const value = invoiceValue(field, source, draft)
          const repeated = Array.isArray(value) && value.length > 1
          const scalar = Array.isArray(value) ? value[0] : value
          const change = (value: ReviewValue) =>
            onChange({ ...draft.field_values, [field.code]: value })
          return (
            <label key={field.code} data-field={field.code}>
              <span>
                {field.label}
                {field.code === 'cargo_places' && (
                  <Tooltip
                    title="Количество упаковок, паллет и других грузовых мест по документу или приёмке"
                    trigger={['hover', 'focus', 'click']}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<InfoCircleOutlined />}
                      aria-label="Что такое грузовые места"
                    />
                  </Tooltip>
                )}
                {uncertain.some(
                  (item: AdditionalField) => invoiceFieldCode(item, definitions) === field.code,
                ) && <Tag color="warning">Проверить</Tag>}
              </span>
              {!field.editable || repeated ? (
                <div
                  className="supply-invoice__readonly"
                  id={`supply-upd-${field.code}`}
                  tabIndex={0}
                >
                  {invoiceDisplay(value, field.value_type)}
                  {repeated && (
                    <small>
                      Несколько значений: исправляются в дополнительных реквизитах по отдельности
                    </small>
                  )}
                </div>
              ) : field.value_type === 'integer' || field.value_type === 'decimal' ? (
                <InputNumber
                  id={`supply-upd-${field.code}`}
                  aria-label={field.label}
                  style={{ width: '100%' }}
                  size="small"
                  stringMode
                  min="0"
                  precision={field.value_type === 'integer' ? 0 : undefined}
                  disabled={disabled}
                  value={scalar == null || scalar === '' ? null : String(scalar)}
                  placeholder="Не указано"
                  onChange={value => change(value)}
                />
              ) : multiline(field.code) ? (
                <Input.TextArea
                  id={`supply-upd-${field.code}`}
                  aria-label={field.label}
                  size="small"
                  disabled={disabled}
                  maxLength={2000}
                  value={String(scalar ?? '')}
                  placeholder="Не указано"
                  autoSize={{ minRows: 2, maxRows: 3 }}
                  onChange={event => change(event.target.value)}
                />
              ) : (
                <Input
                  id={`supply-upd-${field.code}`}
                  aria-label={field.label}
                  size="small"
                  disabled={disabled}
                  maxLength={2000}
                  value={String(scalar ?? '')}
                  placeholder="Не указано"
                  onChange={event => change(event.target.value)}
                />
              )}
            </label>
          )
        })}
      </div>
    </>
  )
  return presentation === 'plain' ? (
    <section className="supply-check__plain-fields">{content}</section>
  ) : (
    <Collapse
      className="supply-check__additional"
      size="small"
      activeKey={activeKey}
      onChange={value => setActiveKey(Array.isArray(value) ? value : [value])}
      items={[{ key: 'upd', label: title, children: content }]}
    />
  )
}
